// hooks/useForm.ts
import React, { SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  CategoryInterface,
  OptionItemInterface,
  ValidationResultInterface,
  ValidationRulesInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { useFiles } from '../../../../../../common/hooks/useFiles'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { v4 } from 'uuid'
import { categoryValidations, minUploadFilesCount } from '../../../../../../common/utils/constants'
import { useInput } from '../../../../../../common/hooks/useInput'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'

const validations: ValidationRulesInterface[] = categoryValidations

export const useForm = (
  data: CategoryInterface,
  setData: React.Dispatch<SetStateAction<CategoryInterface>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  el?: CategoryInterface,
) => {
  const [salesApi] = useState(getSalesApi())
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[] | []>([])
  const [disabled, setDisabled] = useState<boolean>(true)
  const [attrsDisabled, setAttrsDisabled] = useState<boolean>(false)
  const [fieldsDisabled, setFieldsDisabled] = useState<boolean>(false)
  const [categoryList, setCategoryList] = useState<CategoryInterface[]>([])
  const { files, setFiles, pairs, setPairs, dataInit, fetchFiles } = useFiles()
  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )
  const [activeTab, setActiveTab] = useState<'main' | 'attributes' | 'fields'>('main')
  const { inputHandler } = useInput(setData)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()

  useEffect(() => {
    if (el && el.files && el.files?.length > 0) {
      fetchFiles(el.files)
      el.files = []
    }
    if (el) setData(el)
  }, [el])

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const getCategoryList = useCallback(async () => {
    try {
      const result = await salesApi.getCategoryList()
      if (result) setCategoryList(result?.data)
    } catch (error) {}
  }, [salesApi])

  const formAction = useCallback(async () => {
    try {
      await showCommonLoader()
      await delayAction(1000)

      const { id, createdAt, updatedAt, iiko_id, c1_id, moysklad_id, ...restData } = data

      // ============================================================
      // ЧИСТКА ATTRIBUTES — убираем id, clusterId, categoryId
      // ============================================================
      const cleanAttributes =
        data.attributes?.map((attr: any) => {
          const {
            id: attrId,
            clusterId: attrClusterId,
            categoryId: attrCategoryId,
            ...attrWithoutMeta
          } = attr
          return attrWithoutMeta
        }) || []

      // ============================================================
      // ЧИСТКА FIELDS — убираем createdAt, updatedAt, clusterId, categoryId
      // ============================================================
      const cleanFields =
        data.fields?.map((field: any) => {
          const {
            createdAt: fCreatedAt,
            updatedAt: fUpdatedAt,
            clusterId: fClusterId,
            categoryId: fCategoryId,
            ...fieldWithoutMeta
          } = field
          return fieldWithoutMeta
        }) || []

      // ============================================================
      // CHILDREN — маппим в массив объектов { childId }
      // ============================================================
      const cleanChildren = ((data.children || []) as number[]).map((childId) => ({
        childId: Number(childId),
      }))

      const cleanData = {
        ...restData,
        attributes: cleanAttributes,
        fields: cleanFields,
        children: cleanChildren,
      }

      if (data.id) {
        await salesApi.updateCategory({ ...cleanData, id: data.id, file: dataInit(files) })
      } else {
        await salesApi.createCategory({ ...cleanData, file: dataInit(files) })
      }

      await getList()
      await hideCommonLoader()
      setModalActive(false)
    } catch (error) {
      await hideCommonLoader()
    }
  }, [salesApi, data, files, dataInit, getList, setModalActive, showCommonLoader, hideCommonLoader])

  const createCategorySelectList = useCallback(() => {
    const currentId = Number(data.id)

    let arr: OptionItemInterface[] = []
    for (const item of categoryList) {
      if (Number(item.id) === currentId) continue

      let pattern: OptionItemInterface = {
        id: String(item.id),
        label: `${item.name}`,
        name: item.name,
        value: String(item.id),
      }
      arr.push(pattern)
    }
    return arr
  }, [categoryList, data.id])

  const multipleSearchSelectHandler = useCallback(
    (value: string | number | boolean, fieldName: string, type: string = '') => {
      if (!value) return
      const numValue = Number(value)
      if (isNaN(numValue)) return

      setData((prev) => {
        if (fieldName === 'children') {
          const children = (prev.children || []) as number[]
          if (children.includes(numValue)) return prev
          return { ...prev, children: [...children, numValue] }
        }

        return prev
      })
    },
    [setData],
  )

  const multipleSearchSelectRemoveHandler = useCallback(
    (value: string | number | boolean, fieldName: string) => {
      if (!value) return
      const numValue = Number(value)
      if (isNaN(numValue)) return

      setData((prev) => {
        if (fieldName === 'children') {
          const children = (prev.children || []) as number[]
          return {
            ...prev,
            children: children.filter((el) => el !== numValue),
          }
        }

        return prev
      })
    },
    [setData],
  )

  useEffect(() => {
    getCategoryList()
  }, [getCategoryList])

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        checkAndAddValidation(item)
      }
    }
  }, [checkAndAddValidation, data])

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item])
      }
    }
  }, [data])

  useEffect(() => {
    setDisabled(
      Boolean(files.length < minUploadFilesCount || validationResult.find((el) => el.notValid)),
    )
  }, [files, validationResult])

  useEffect(() => {
    if (!data.isComplex && data.id) {
      setData((prev) => ({ ...prev, fields: [] }))
    }
  }, [data.isComplex, data.id, setData])

  return {
    formAction,
    data,
    inputHandler,
    setDirty,
    getValidationResult,
    files,
    pairs,
    setFiles,
    setPairs,
    createCategorySelectList,
    multipleSearchSelectHandler,
    multipleSearchSelectRemoveHandler,
    disabled,
    activeTab,
    setActiveTab,
    attrsDisabled,
    setAttrsDisabled,
    fieldsDisabled,
    setFieldsDisabled,
  }
}
